import { useState, useEffect, useRef } from "react";
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/outsourced`;
const PRIMARY = "bg-[#5990DC]";

const systems = ['TB', 'YDL', 'WF', 'MC'];
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const defaultPeriod = () => {
  const n = new Date();
  return { mode: 'month', month: n.getMonth() + 1, year: n.getFullYear(), day: ymd(n), from: '', to: '' };
};

function periodBounds(p) {
  if (p.mode === 'month') {
    const last = new Date(p.year, p.month, 0).getDate();
    return { from: `${p.year}-${pad(p.month)}-01`, to: `${p.year}-${pad(p.month)}-${pad(last)}` };
  }
  if (p.mode === 'year') return { from: `${p.year}-01-01`, to: `${p.year}-12-31` };
  if (p.mode === 'day') return { from: p.day || '0000-01-01', to: p.day || '9999-12-31' };
  return { from: p.from || '0000-01-01', to: p.to || '9999-12-31' };
}

function periodLabel(p) {
  if (p.mode === 'month') return `${monthNames[p.month - 1]} ${p.year}`;
  if (p.mode === 'year') return `${p.year}`;
  if (p.mode === 'day') return p.day || 'Select a day';
  return `${p.from || 'start'} to ${p.to || 'today'}`;
}

function periodFileTag(p) {
  if (p.mode === 'month') return `${p.year}-${pad(p.month)}`;
  if (p.mode === 'year') return `${p.year}`;
  if (p.mode === 'day') return p.day || 'day';
  return `${p.from || 'start'}_to_${p.to || 'end'}`;
}

const fmtShort = (d) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
const fmtLong = (d) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }) : '-');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-');
const userName = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}').name || ''; } catch (e) { return ''; }
};

function PeriodControls({ value, onChange }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 3 + i);
  const update = (patch) => onChange({ ...value, ...patch });
  return (
    <div>
      <label className="text-xs font-bold text-gray-700 mb-1.5 block">Period</label>
      <div className="flex gap-1.5">
        <select value={value.mode} onChange={(e) => update({ mode: e.target.value })} className="border rounded-lg px-2 py-2 text-sm">
          <option value="month">Month</option>
          <option value="year">Year</option>
          <option value="day">Day</option>
          <option value="range">Custom</option>
        </select>
        {value.mode === 'month' && (
          <>
            <select value={value.month} onChange={(e) => update({ month: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0">
              {monthNames.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
            </select>
            <select value={value.year} onChange={(e) => update({ year: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm">
              {years.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </>
        )}
        {value.mode === 'year' && (
          <select value={value.year} onChange={(e) => update({ year: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm flex-1">
            {years.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
        )}
        {value.mode === 'day' && (
          <input type="date" value={value.day} onChange={(e) => update({ day: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
        )}
        {value.mode === 'range' && (
          <>
            <input type="date" value={value.from} onChange={(e) => update({ from: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
            <input type="date" value={value.to} onChange={(e) => update({ to: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
          </>
        )}
      </div>
    </div>
  );
}

function AccountsBanner({ accounts }) {
  const down = (accounts || []).filter((a) => !a.ok);
  if (down.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700">
      {down.map((a) => a.system).join(', ')} could not be reached, so {down.length > 1 ? 'their' : 'its'} numbers are not included right now. Press Refresh to try again.
    </div>
  );
}

const actionNames = ['Initial Sync', 'Update Current Batch', 'New Batch', 'New Date', 'Undo'];

function LogView() {
  const [period, setPeriod] = useState(defaultPeriod());
  const [account, setAccount] = useState('');
  const [action, setAction] = useState('');
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/audit`, { cache: 'no-store' });
      const data = await res.json();
      setEntries(Array.isArray(data.entries) ? data.entries : []);
      setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
    } catch (e) {
      setEntries([]);
      setAccounts([]);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const undo = async (e) => {
    const where = e.production_date ? `${fmtLong(e.production_date)} B${e.batch_number}` : 'its batch';
    if (!window.confirm(`Undo this sync?\n\n${e.account}: ${e.action}, ${e.new_sheets} sheets will be removed from ${where}.`)) return;
    setBusy(e.audit_id);
    setMsg('');
    try {
      const res = await fetch(`${BASE}/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ system: e.account, audit_id: e.audit_id, user: userName() }),
      });
      const data = await res.json().catch(() => ({}));
      setMsg(res.ok ? 'Undone. The files can be synced again from Production.' : data.error || data.detail || 'Undo failed.');
    } catch (err) {
      setMsg('Undo failed.');
    }
    setBusy('');
    await load();
  };

  const { from, to } = periodBounds(period);
  const view = entries.filter((e) => {
    const d = String(e.ts).slice(0, 10);
    if (d < from || d > to) return false;
    if (account && e.account !== account) return false;
    if (action && e.action !== action) return false;
    return true;
  });

  return (
    <div>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-xl font-semibold mb-1">Outsourced Work → Sync Data</h1>
          <p className="text-gray-500 text-sm">Every sync, in order. Syncing itself is done inside each production system: PDF History, then SYNC.</p>
        </div>
        <button onClick={load} disabled={loading} className={`${PRIMARY} text-white rounded-lg px-6 py-2.5 text-sm font-medium disabled:opacity-50`}>
          {loading ? 'Refreshing...' : '🔄 Refresh'}
        </button>
      </div>
      <div className="mb-4"></div>

      <AccountsBanner accounts={accounts} />
      {msg && <p className="text-sm text-green-700 font-medium mb-3">{msg}</p>}

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[320px]">
            <PeriodControls value={period} onChange={setPeriod} />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 mb-1.5 block">Account</label>
            <select value={account} onChange={(e) => setAccount(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All Accounts</option>
              {systems.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 mb-1.5 block">Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All</option>
              {actionNames.map((a) => (<option key={a} value={a}>{a}</option>))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
              <th className="text-left px-3 py-2.5 font-normal">Date</th>
              <th className="text-left px-3 py-2.5 font-normal">Time</th>
              <th className="text-left px-3 py-2.5 font-normal">Account</th>
              <th className="text-left px-3 py-2.5 font-normal">Action</th>
              <th className="text-left px-3 py-2.5 font-normal">Production Date</th>
              <th className="text-left px-3 py-2.5 font-normal">Batch</th>
              <th className="text-right px-3 py-2.5 font-normal">New Files</th>
              <th className="text-right px-3 py-2.5 font-normal">New Sheets</th>
              <th className="text-left px-3 py-2.5 font-normal">User</th>
              <th className="text-left px-3 py-2.5 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="text-center text-gray-400 py-10">Loading...</td></tr>
            ) : view.length === 0 ? (
              <tr><td colSpan={10} className="text-center text-gray-400 py-10">No syncs found for these filters.</td></tr>
            ) : (
              view.map((e) => (
                <tr key={`${e.account}-${e.audit_id}`} className={`border-b border-gray-50 ${e.undone ? 'text-gray-400' : ''}`}>
                  <td className="px-3 py-2.5">{fmtLong(String(e.ts).slice(0, 10))}</td>
                  <td className="px-3 py-2.5">{fmtTime(e.ts)}</td>
                  <td className="px-3 py-2.5 font-medium">{e.account}</td>
                  <td className="px-3 py-2.5">{e.action}</td>
                  <td className="px-3 py-2.5">{e.production_date ? fmtLong(e.production_date) : '-'}</td>
                  <td className="px-3 py-2.5">{e.batch_number ? `B${e.batch_number}` : '-'}</td>
                  <td className="px-3 py-2.5 text-right">{e.new_files}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{e.new_sheets}</td>
                  <td className="px-3 py-2.5">{e.user || '-'}</td>
                  <td className="px-3 py-2.5">
                    {e.action === 'Undo' ? (
                      <span className="text-xs text-gray-400">Reverted</span>
                    ) : e.undone ? (
                      <span className="text-xs text-gray-400">Undone</span>
                    ) : (
                      <button onClick={() => undo(e)} disabled={busy === e.audit_id} className="text-xs border border-gray-300 rounded-lg px-3 py-1 hover:bg-gray-50 disabled:opacity-50">
                        {busy === e.audit_id ? '...' : 'Undo'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LogView;
